import { ITeam } from '../../interfaces/teams/ITeam.interface';

const UPPERCASE_DIVISIONS: ReadonlySet<string> = new Set(['SCALAR']);

class Team implements ITeam {
  readonly raw: string;
  readonly name: string;
  readonly division: string;
  readonly namespace: string;
  readonly picture: string;
  readonly portalTeamPageHref: string;
  readonly nameInGitlab: string;
  readonly divisionSlug: string;
  readonly teamSlug: string;

  private constructor(
    ref: string,
    displayName: string | undefined,
    namespace: string,
    picture: string,
    parentRef?: string,
  ) {
    this.raw = Team.#extractRaw(ref);

    const rawWithoutPrefix = this.raw.replace(/^teams-/, '');

    this.divisionSlug = parentRef?.replace(/^teams-/, '') ?? '';
    this.division = this.divisionSlug
      ? Team.#formatDivisionName(this.divisionSlug)
      : Team.#formatDivisionName(rawWithoutPrefix.split('-')[0]);

    this.teamSlug = this.divisionSlug
      ? rawWithoutPrefix.replace(`${this.divisionSlug}-`, '')
      : rawWithoutPrefix.split('-').slice(1).join('-');

    const teamNameParts = this.teamSlug.split('-');

    this.name = displayName || Team.toTitleCase(teamNameParts);
    this.namespace = namespace;
    this.picture = picture;
    this.portalTeamPageHref = `/catalog/${this.namespace}/group/${this.raw}`;
    this.nameInGitlab = this.teamSlug;
  }

  static createIfCorrectFormat(
    ref: string,
    displayName: string | undefined,
    namespace: string,
    picture: string,
    currentUserRef?: string,
    parentRef?: string,
  ): Team | null {
    const raw = Team.#extractRaw(ref);

    if (!raw.startsWith('teams-')) {
      return null;
    }

    const parts = raw.split('-').slice(1);
    if (parts.length < 2) {
      return null;
    }

    if (Team.#isExcluded(raw, currentUserRef)) {
      return null;
    }

    return new Team(ref, displayName, namespace, picture, parentRef);
  }

  static toTitleCase(words: string[] | string): string {
    let wordArray: string[];
    if (typeof words === 'string') {
      wordArray = words.split('-');
    } else {
      wordArray = words;
    }

    return wordArray
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  static #isExcluded(ref: string, currentUserRef?: string): boolean {
    return currentUserRef ? ref.includes(currentUserRef) : false;
  }

  static #extractRaw(ref: string): string {
    return ref.toLowerCase().split('/').pop() ?? '';
  }

  static #formatDivisionName(division: string): string {
    const upper = division.toUpperCase();
    return UPPERCASE_DIVISIONS.has(upper) ? upper : Team.toTitleCase(division);
  }
}

export { Team };
